import { supabase } from './supabase';
import { calculateFromTTC } from '@/utils/taxCalculator';

export interface CreditNote {
  id: string;
  order_id?: string;
  client_id?: string;
  vendor_id?: string;
  store_id?: string; // ID du point de vente
  credit_note_type?: string;
  credit_note_date: string;
  issue_date?: string;
  total_htva?: number;
  total_tva?: number;
  total_ttc?: number;
  status?: string;
  created_at?: string;
  credit_note_number?: string;
  reason?: string;
  delivery_info?: any;
  cloning_disabled?: boolean;
  store_name?: string;
  store_initiales?: string;
}

export interface CreditNoteDetail {
  id: string;
  credit_note_id?: string;
  order_item_id?: string;
  article_id?: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  total_price: number;
  article_name?: string;
  article_code?: string;
  article_reference?: string;
  article_description?: string;
  tax_rate?: number;
  reason_detail?: string;
}

export interface CreditNoteRefund {
  id: string;
  credit_note_id?: string;
  refund_date: string;
  refund_method?: string;
  amount: number;
  refund_type?: string;
  created_at?: string;
  notes?: string;
}

class CreditNoteService {
  // =====================================================
  // CRÉATION ET RÉCUPÉRATION DES NOTES DE CRÉDIT
  // =====================================================

  async createCreditNote(creditNoteData: Partial<CreditNote>): Promise<CreditNote | null> {
    try {
      const { data, error } = await supabase
        .from('credit_notes')
        .insert([creditNoteData])
        .select()
        .single();

      if (error) {
        console.error('❌ Erreur lors de la création de la note de crédit:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la création de la note de crédit:', error);
      throw error;
    }
  }

  async getCreditNoteById(id: string): Promise<CreditNote | null> {
    try {
      const { data, error } = await supabase.from('credit_notes').select('*').eq('id', id).single();

      if (error) {
        console.error('❌ Erreur lors de la récupération de la note de crédit:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la note de crédit:', error);
      throw error;
    }
  }

  async getAllCreditNotes(storeFilter?: string): Promise<CreditNote[]> {
    try {
      let query = supabase.from('credit_notes').select(
        `
          *,
          clients:client_id(full_name, email),
          stores:store_id(name, initiales)
        `
      );

      // Filtre par point de vente si spécifié
      if (storeFilter) {
        query = query.eq('store_id', storeFilter);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erreur avec jointure, tentative sans relations:', error);

        // Fallback: requête sans jointure si les relations ne sont pas configurées
        let simpleQuery = supabase.from('credit_notes').select('*');

        if (storeFilter) {
          simpleQuery = simpleQuery.eq('store_id', storeFilter);
        }

        const { data: simpleData, error: simpleError } = await simpleQuery.order('created_at', {
          ascending: false,
        });

        if (simpleError) {
          console.error('❌ Erreur lors de la récupération des notes de crédit:', simpleError);
          throw simpleError;
        }

        // Enrichir avec les données clients
        if (simpleData && simpleData.length > 0) {
          const enrichedData = await Promise.all(
            simpleData.map(async (note: any) => {
              if (note.client_id) {
                const { data: client } = await supabase
                  .from('clients')
                  .select('full_name, email')
                  .eq('id', note.client_id)
                  .single();

                if (client) {
                  note.clients = client;
                }
              }
              return note;
            })
          );
          return enrichedData;
        }

        return simpleData || [];
      }

      // Transformer les données pour aplatir les relations stores
      const transformedData = (data || []).map((note: any) => ({
        ...note,
        store_name: note.stores?.name,
        store_initiales: note.stores?.initiales,
      }));

      return transformedData;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des notes de crédit:', error);
      throw error;
    }
  }

  async updateCreditNote(id: string, updates: Partial<CreditNote>): Promise<CreditNote | null> {
    try {
      const { data, error } = await supabase
        .from('credit_notes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Erreur lors de la mise à jour de la note de crédit:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de la note de crédit:', error);
      throw error;
    }
  }

  async deleteCreditNote(id: string): Promise<boolean> {
    try {
      console.log('🗑️ Suppression transactionnelle de la note de crédit:', id);

      const { error } = await supabase.rpc('delete_credit_note', {
        p_credit_note_id: id,
      });

      if (error) {
        console.error('❌ Erreur lors de la suppression de la note de crédit:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur dans deleteCreditNote:', error);
      throw error;
    }
  }

  // =====================================================
  // GESTION DES DÉTAILS DE NOTES DE CRÉDIT
  // =====================================================

  async getCreditNoteDetails(creditNoteId: string): Promise<CreditNoteDetail[]> {
    try {
      const { data, error } = await supabase
        .from('credit_notes_details')
        .select('*')
        .eq('credit_note_id', creditNoteId)
        .order('id', { ascending: true });

      if (error) {
        console.error('❌ Erreur lors de la récupération des détails:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des détails:', error);
      throw error;
    }
  }

  async addCreditNoteDetail(
    detailData: Partial<CreditNoteDetail>
  ): Promise<CreditNoteDetail | null> {
    try {
      const { data, error } = await supabase
        .from('credit_notes_details')
        .insert([detailData])
        .select()
        .single();

      if (error) {
        console.error("❌ Erreur lors de l'ajout du détail:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("❌ Erreur lors de l'ajout du détail:", error);
      throw error;
    }
  }

  async updateCreditNoteDetail(
    id: string,
    updates: Partial<CreditNoteDetail>
  ): Promise<CreditNoteDetail | null> {
    try {
      const { data, error } = await supabase
        .from('credit_notes_details')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Erreur lors de la mise à jour du détail:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du détail:', error);
      throw error;
    }
  }

  async deleteCreditNoteDetail(id: string): Promise<boolean> {
    try {
      const { error } = await supabase.from('credit_notes_details').delete().eq('id', id);

      if (error) {
        console.error('❌ Erreur lors de la suppression du détail:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du détail:', error);
      throw error;
    }
  }

  // =====================================================
  // GESTION DES REMBOURSEMENTS
  // =====================================================

  async getCreditNoteRefunds(creditNoteId: string): Promise<CreditNoteRefund[]> {
    try {
      const { data, error } = await supabase
        .from('credit_notes_refunds')
        .select('*')
        .eq('credit_note_id', creditNoteId)
        .order('refund_date', { ascending: false });

      if (error) {
        console.error('❌ Erreur lors de la récupération des remboursements:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des remboursements:', error);
      throw error;
    }
  }

  async addCreditNoteRefund(
    refundData: Partial<CreditNoteRefund>
  ): Promise<CreditNoteRefund | null> {
    try {
      const { data, error } = await supabase
        .from('credit_notes_refunds')
        .insert([refundData])
        .select()
        .single();

      if (error) {
        console.error("❌ Erreur lors de l'ajout du remboursement:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("❌ Erreur lors de l'ajout du remboursement:", error);
      throw error;
    }
  }

  async getTotalRefundsByCreditNoteId(creditNoteId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('credit_notes_refunds')
        .select('amount')
        .eq('credit_note_id', creditNoteId);

      if (error) {
        console.error('❌ Erreur lors du calcul du total des remboursements:', error);
        throw error;
      }

      const total = data?.reduce((sum, refund) => sum + (refund.amount || 0), 0) || 0;
      return total;
    } catch (error) {
      console.error('❌ Erreur lors du calcul du total des remboursements:', error);
      throw error;
    }
  }

  // =====================================================
  // MISE À JOUR DES TOTAUX
  // =====================================================

  async updateCreditNoteTotals(creditNoteId: string): Promise<boolean> {
    try {
      // Récupérer tous les détails de la note de crédit
      const details = await this.getCreditNoteDetails(creditNoteId);

      // Calculer les totaux
      const totals = details.reduce(
        (acc, detail) => {
          const lineTotal = detail.total_price || 0;
          const taxRate = detail.tax_rate || 21;
          const { htva, tva } = calculateFromTTC(lineTotal, taxRate);

          return {
            total_ttc: acc.total_ttc + lineTotal,
            total_htva: acc.total_htva + htva,
            total_tva: acc.total_tva + tva,
          };
        },
        { total_ttc: 0, total_htva: 0, total_tva: 0 }
      );

      // Mettre à jour la note de crédit avec les nouveaux totaux
      const success = await this.updateCreditNote(creditNoteId, {
        total_htva: totals.total_htva,
        total_tva: totals.total_tva,
        total_ttc: totals.total_ttc,
      });

      if (success) {
        return true;
      } else {
        console.error('❌ Échec de la mise à jour des totaux');
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour des totaux:', error);
      throw error;
    }
  }

  // =====================================================
  // RECHERCHE ET FILTRAGE
  // =====================================================

  async searchCreditNotes(searchTerm: string, storeFilter?: string): Promise<CreditNote[]> {
    try {
      let query = supabase.from('credit_notes').select(
        `
          *,
          clients:client_id(full_name, email),
          stores:store_id(name, initiales)
        `
      );

      // Filtre par point de vente si spécifié
      if (storeFilter) {
        query = query.eq('store_id', storeFilter);
      }

      const { data, error } = await query
        .or(
          `
          credit_note_number.ilike.%${searchTerm}%,
          clients.full_name.ilike.%${searchTerm}%
        `
        )
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erreur lors de la recherche:', error);
        throw error;
      }

      // Transformer les données pour aplatir les relations stores
      const transformedData = (data || []).map((note: any) => ({
        ...note,
        store_name: note.stores?.name,
        store_initiales: note.stores?.initiales,
      }));

      return transformedData;
    } catch (error) {
      console.error('❌ Erreur lors de la recherche:', error);
      throw error;
    }
  }

  async getCreditNotesByStatus(status: string): Promise<CreditNote[]> {
    try {
      const { data, error } = await supabase
        .from('credit_notes')
        .select(
          `
          *,
          clients!credit_notes_client_id_fkey(full_name, email),
          vendors!credit_notes_vendor_id_fkey(name, initiales)
        `
        )
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erreur lors de la récupération par statut:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération par statut:', error);
      throw error;
    }
  }

  /**
   * Générer un numéro de note de crédit unique avec préfixe du magasin
   * Format: Préfixe + Année (2 chiffres) + ID (5 chiffres)
   * Exemple: NCL2500001 (NCL = préfixe Liège, 25 = année 2025, 00001 = ID)
   */
  async generateCreditNoteNumber(storeId?: string): Promise<string> {
    const today = new Date();
    const year = String(today.getFullYear()).slice(-2); // 2 derniers chiffres de l'année

    // Récupérer le préfixe du magasin
    let prefix = 'NC'; // Préfixe par défaut si pas de magasin

    if (storeId) {
      const { data: storeData, error: storeError } = await supabase
        .from('stores')
        .select('credit_note_prefix')
        .eq('id', storeId)
        .single();

      if (!storeError && storeData?.credit_note_prefix) {
        prefix = storeData.credit_note_prefix;
      }
    }

    // Récupérer le dernier ID pour ce préfixe et cette année
    const pattern = `${prefix}${year}%`;
    const { data, error } = await supabase
      .from('credit_notes')
      .select('credit_note_number')
      .like('credit_note_number', pattern)
      .order('credit_note_number', { ascending: false })
      .limit(1);

    if (error) throw error;

    let nextId = 1;
    if (data && data.length > 0) {
      const lastNumber = data[0].credit_note_number;
      // Extraire les 5 derniers chiffres (l'ID)
      const lastId = parseInt(lastNumber.slice(-5));
      nextId = lastId + 1;
    }

    // Format: Préfixe + Année (2 chiffres) + ID (5 chiffres)
    return `${prefix}${year}${String(nextId).padStart(5, '0')}`;
  }
}

export const creditNoteService = new CreditNoteService();
